import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { callFunction } from '@/lib/api';
import { useActiveClinic } from '@/lib/ActiveClinicContext';

/**
 * EnvelopeTypes the SOR picker may filter on.
 * Must stay aligned with `SOR_LOOKUP_RECEIVES` in
 * `supabase/functions/communicate-sor-lookup/index.ts`.
 */
export const SOR_LOOKUP_RECEIVES = ['XREF01', 'XREF02', 'XREF06', 'REF06'] as const;
export type SorLookupReceives = (typeof SOR_LOOKUP_RECEIVES)[number];

export type SorOrganisation = {
  sorId: string;
  name: string;
  ean: string;
  shakId: string | null;
  providerId: string | null;
  speciality: string | null;
  address: { street: string | null; postCode: string | null; district: string | null };
  /** Resolved per the receiverIdentity rule. null when the row cannot be used as a MedCom receiver. */
  identifier: string | null;
  identifierCode: 'sygehusafdelingsnummer' | 'ydernummer' | null;
  /** SOR EnvelopeType values the unit's EAN accepts. */
  ediReceiveTypes: string[];
  /** SOR id of the parent unit. Henvisningshotellet's units share one parent. */
  parentSorId: string | null;
};

type SorLookupResult = {
  organizations: SorOrganisation[];
};

const SEARCH_DEBOUNCE_MS = 300;
const MIN_SEARCH_LENGTH = 2;

function useDebouncedValue(value: string, delayMs: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handle = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(handle);
  }, [value, delayMs]);
  return debounced;
}

export function useSorSearch(
  query: string,
  /** null searches every unit (the epikrise copy receiver); the caller filters. */
  receives: SorLookupReceives | readonly SorLookupReceives[] | null,
): { data: SorOrganisation[] | undefined; isLoading: boolean; error: unknown } {
  const { activeClinicId } = useActiveClinic();
  const debouncedQuery = useDebouncedValue(query.trim(), SEARCH_DEBOUNCE_MS);
  const enabled = Boolean(activeClinicId) && debouncedQuery.length >= MIN_SEARCH_LENGTH;
  const receivesKey =
    receives === null ? 'any' : typeof receives === 'string' ? receives : receives.join(',');

  const result = useQuery({
    queryKey: ['sor-lookup', 'search', activeClinicId, debouncedQuery, receivesKey] as const,
    enabled,
    staleTime: 60_000,
    queryFn: async (): Promise<SorOrganisation[]> => {
      if (!activeClinicId || debouncedQuery.length < MIN_SEARCH_LENGTH) return [];
      const data = await callFunction<
        {
          clinicId: string;
          mode: 'search';
          q: string;
          receives?: SorLookupReceives | readonly SorLookupReceives[];
        },
        SorLookupResult
      >('communicate-sor-lookup', {
        clinicId: activeClinicId,
        mode: 'search',
        q: debouncedQuery,
        ...(receives === null ? {} : { receives }),
      });
      return data.organizations;
    },
  });

  return {
    data: enabled ? result.data : undefined,
    isLoading: result.isLoading,
    error: result.error,
  };
}

export function useSorBySorId(
  sorId: string | null,
): { data: SorOrganisation | null | undefined; isLoading: boolean; error: unknown } {
  const { activeClinicId } = useActiveClinic();
  const trimmed = sorId?.trim() ?? '';
  const enabled = Boolean(activeClinicId) && trimmed.length > 0;

  const result = useQuery({
    queryKey: ['sor-lookup', 'sorId', activeClinicId, trimmed] as const,
    enabled,
    staleTime: 60_000,
    queryFn: async (): Promise<SorOrganisation | null> => {
      if (!activeClinicId || trimmed.length === 0) return null;
      const data = await callFunction<
        { clinicId: string; mode: 'sorId'; sorId: string },
        SorLookupResult
      >('communicate-sor-lookup', {
        clinicId: activeClinicId,
        mode: 'sorId',
        sorId: trimmed,
      });
      return data.organizations[0] ?? null;
    },
  });

  return {
    data: enabled ? result.data : undefined,
    isLoading: result.isLoading,
    error: result.error,
  };
}
